import { useCallback, useEffect, useRef, useState } from "react";

// Reusable multi-selection for any list page. Pass the *ordered, filtered* list of ids
// (memoized) so shift-range and "select all" track exactly what's on screen, and so the
// selection self-prunes when search/filters hide an item — a selected id can never point
// at something the user can't see.
const useSelection = (orderedIds = []) => {
  const [selected, setSelected] = useState(() => new Set());
  const anchorRef = useRef(null);

  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const allowed = new Set(orderedIds);
      const next = new Set();
      let changed = false;
      for (const id of prev) {
        if (allowed.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [orderedIds]);

  const isSelected = useCallback((id) => selected.has(id), [selected]);

  const clear = useCallback(() => {
    anchorRef.current = null;
    setSelected(new Set());
  }, []);

  const selectAll = useCallback(() => setSelected(new Set(orderedIds)), [orderedIds]);

  const select = useCallback(
    (index, { shiftKey = false } = {}) => {
      const id = orderedIds[index];
      if (id == null) return;

      const anchorIndex = anchorRef.current == null ? -1 : orderedIds.indexOf(anchorRef.current);
      if (shiftKey && anchorIndex !== -1) {
        const start = Math.min(anchorIndex, index);
        const end = Math.max(anchorIndex, index);
        setSelected((prev) => {
          const next = new Set(prev);
          for (let i = start; i <= end; i++) next.add(orderedIds[i]);
          return next;
        });
        return;
      }

      anchorRef.current = id;
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [orderedIds],
  );

  return {
    selected,
    count: selected.size,
    allSelected: selected.size > 0 && selected.size === orderedIds.length,
    isSelected,
    select,
    selectAll,
    clear,
  };
};

export default useSelection;
